import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { GlassCard } from '@/components/GlassCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Eye, EyeOff, Camera, X } from 'lucide-react';
import { fileToDataUrl } from '@/lib/utils';
import { uploadImage } from '@/lib/mediaUpload';
import { Seo } from '@/components/Seo';

export default function SignupPage() {
  const nav = useNavigate();
  const { signup, refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [agree21, setAgree21] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = React.useRef();

  const onAvatarFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await fileToDataUrl(f, 400, 0.8);
      setAvatar(dataUrl);
    } catch {
      toast.error('Could not read that image');
    }
    e.target.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree21) {
      toast.error('You must confirm you are 21+ to use Thirrsty venues.');
      return;
    }
    setLoading(true);
    try {
      await signup(email.trim(), password, displayName.trim());
      if (avatar) {
        try {
          const url = await uploadImage(avatar);
          await api.patch('/users/me', { avatar_url: url });
          await refresh();
        } catch {}
      }
      toast.success('Welcome to Thirrsty.com');
      nav('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo
        title="Sign Up — Free Profile + QR Discounts | Thirrsty.com"
        description="Create a free Thirrsty profile and get your personal QR code. Unlock instant discounts at partner bars, clubs, lounges, coffee shops, vineyards, breweries, and frat parties across the USA."
        path="/auth/signup"
      />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <div className="mb-6"><Logo asDownloadTrigger /></div>
        <GlassCard className="p-6 th-float-in">
          <h1 className="font-display text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Create your account</h1>
          <p className="mt-1 text-sm text-white/70">Free profile · personal QR for venue discounts.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B24A]/70"
                aria-label="Add a profile picture"
                data-testid="signup-avatar-button"
              >
                <Avatar className="h-20 w-20 ring-2 ring-[#F6B24A]/50 ring-offset-2 ring-offset-transparent">
                  <AvatarImage src={avatar || undefined} />
                  <AvatarFallback className="bg-white/10 text-white/60">
                    <Camera className="h-7 w-7" />
                  </AvatarFallback>
                </Avatar>
                <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-[#F6B24A] text-[#1A1208] shadow">
                  <Camera className="h-3.5 w-3.5" />
                </span>
              </button>
              {avatar ? (
                <button
                  type="button"
                  onClick={() => setAvatar(null)}
                  className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
                  data-testid="signup-avatar-remove-button"
                >
                  <X className="h-3 w-3" /> Remove photo
                </button>
              ) : (
                <p className="text-xs text-white/50">Add a profile photo (optional)</p>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarFile} data-testid="signup-avatar-input" />
            </div>
            <div>
              <Label htmlFor="name" className="text-white/80">Display name</Label>
              <Input
                id="name" required
                value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                data-testid="auth-display-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@thirrsty.com"
                className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password" type={showPassword ? 'text' : 'password'} required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-11 bg-white/5 border-white/12 pr-11 text-white placeholder:text-white/40"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-white/50 hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="auth-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input
                type="checkbox" checked={agree21}
                onChange={(e) => setAgree21(e.target.checked)}
                className="h-4 w-4 rounded border-white/30 bg-white/10 text-[#F6B24A]"
                data-testid="auth-21-plus-checkbox"
              />
              I confirm I'm 21+ where alcohol is served.
            </label>
            <Button
              type="submit" disabled={loading}
              data-testid="auth-signup-submit-button"
              className="h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 th-spin" /> : 'Create account'}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-white/60">
            Already have an account?{' '}
            <Link to="/auth/login" className="text-[#F6B24A] hover:underline" data-testid="auth-go-login-link">Sign in</Link>
          </p>
        </GlassCard>
      </div>
    </div>
  );
}
